// /discounts — Discount Rules Configuration (PAGES.md §3.91)
//
// Configure and manage all discount types: item-level, bill-level,
// promotional, coupon-based, and role limits. Tabbed interface.
// SHARED vertical. No feature flag. Min role: OWNER (50).
// Traceability: FR-BILL-010, FR-BILL-011, FR-BILL-050, FR-BILL-051

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  Input,
  DatePicker,
  Tabs,
  Toggle,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  DataTable,
  Pagination,
  Modal,
  ConfirmDialog,
  RadioGroup,
  Checkbox,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { getSubunitMultiplier } from '@bbpos/utils';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { ROLE_LEVELS } from '@/types/user';
import { useAuthStore } from '@/stores/use-auth-store';
import { discountRuleService } from '@/services/discount-rule.service';
import { productService } from '@/services/product.service';
import { categoryService } from '@/services/category.service';
import { locationService } from '@/services/location.service';
import type { Location } from '@/types/location';
import type {
  DiscountRule,
  CreateDiscountRuleDto,
  RuleType,
  DiscountType,
  PromoType,
  DiscountConfig,
  CustomerSegment,
} from '@/services/discount-rule.service';
import { ApiError } from '@/lib/api-client';
import { GLASS, GLASS_FILTER, GLASS_FORM, GLASS_INTERACTIVE, cn, toneClasses } from '@/constants/design-tokens';
import { ScheduleFields } from './components/RuleConditionsField';
import { locationIdsForSave, scopeFromRule, storeCountLabel, storeOptions, type StoreScope } from './store-scope';

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

type TabKey = 'ITEM_LEVEL' | 'BILL_LEVEL' | 'PROMOTIONAL' | 'ROLE_LIMITS';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'ITEM_LEVEL', label: 'Item-Level' },
  { key: 'BILL_LEVEL', label: 'Bill-Level' },
  { key: 'PROMOTIONAL', label: 'Promotional' },
  { key: 'ROLE_LIMITS', label: 'Role Limits' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'DISABLED', label: 'Disabled' },
];

const RULE_TYPE_OPTIONS = [
  { value: 'ITEM_LEVEL', label: 'Item-Level' },
  { value: 'BILL_LEVEL', label: 'Bill-Level' },
  { value: 'PROMOTIONAL', label: 'Promotional' },
];

const buildDiscountTypeOptions = (symbol: string) => [
  { value: 'percentage', label: 'Percentage (%)' },
  { value: 'flat', label: `Flat Amount (${symbol})` },
];

const PROMO_TYPE_OPTIONS = [
  { value: 'percentage_off', label: 'Percentage Off' },
  { value: 'flat_off', label: 'Flat Off' },
  { value: 'buy_x_get_y', label: 'Buy X Get Y' },
  { value: 'combo_price', label: 'Combo Price' },
];

const CUSTOMER_SEGMENT_OPTIONS = [
  { value: 'ALL', label: 'All customers' },
  { value: 'NEW', label: 'New customers only' },
  { value: 'LOYAL', label: 'Loyalty members' },
  { value: 'CORPORATE', label: 'Corporate accounts' },
];

const STORE_SCOPE_OPTIONS: { value: StoreScope; label: string }[] = [
  { value: 'ALL', label: 'All stores' },
  { value: 'SELECTED', label: 'Selected stores' },
];

const STATUS_BADGE: Record<string, 'success' | 'info' | 'draft' | 'danger'> = {
  ACTIVE: 'success',
  SCHEDULED: 'info',
  EXPIRED: 'draft',
  DISABLED: 'danger',
};

// ─── Icons ─────────────────────────────────────────────────────────────────

function PercentIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 5L5 19M9 7a2 2 0 11-4 0 2 2 0 014 0zm10 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function PercentEmptyIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 5L5 19M9 7a2 2 0 11-4 0 2 2 0 014 0zm10 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );
}

function PowerIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 11-12.728 0M12 3v9" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    // A bare 'YYYY-MM-DD' is a calendar day; read it as local midnight, not UTC.
    return new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00` : iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

// Format a Date to YYYY-MM-DD from LOCAL parts. toISOString() would shift the
// date back a day in UTC+ timezones (e.g. IST), so a clicked 10th saves as the 9th.
function toLocalIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function todayISO(): string {
  return toLocalIsoDate(new Date());
}

function displayValue(rule: DiscountRule, currency: CurrencyCode): string {
  if (rule.promo_type === 'buy_x_get_y') return `Buy ${rule.buy_quantity ?? 0} Get ${rule.get_quantity ?? 0}`;
  if (rule.promo_type === 'combo_price') return formatPaise(rule.combo_price ?? 0, currency);
  return rule.discount_type === 'percentage' ? `${rule.value}%` : formatPaise(rule.value, currency);
}

/** A combo is priced by its combo price, not by discount_type, which its form never sets. */
function typeLabel(rule: DiscountRule): string {
  if (rule.promo_type === 'combo_price') return 'Combo';
  return rule.discount_type === 'percentage' ? 'Percentage' : 'Flat';
}

// ─── Mobile Card ────────────────────────────────────────────────────────────

function RuleMobileCard({
  rule,
  canModify,
  onEdit,
  onDelete,
  onDuplicate,
  onToggleActive,
  togglePending,
}: {
  rule: DiscountRule;
  canModify: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onToggleActive: () => void;
  togglePending: boolean;
}) {
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  return (
    <div className={cn(GLASS_INTERACTIVE, 'p-4 animate-slide-up')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-base font-semibold text-neutral-900 dark:text-neutral-100">{rule.name}</p>
            <Badge variant={STATUS_BADGE[rule.status] ?? 'draft'} size="sm">{rule.status}</Badge>
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <Badge variant="draft" size="sm">
              {rule.promo_type === 'combo_price' ? 'Combo' : rule.discount_type === 'percentage' ? '%' : symbol}
            </Badge>
            <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
              {displayValue(rule, currency)}
            </span>
            {storeCountLabel(rule.location_ids?.length ?? 0) && (
              <Badge variant="info" size="sm">{storeCountLabel(rule.location_ids?.length ?? 0)}</Badge>
            )}
          </div>
          {(rule.start_date || rule.end_date) && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              {rule.start_date ? formatDate(rule.start_date) : '—'} → {rule.end_date ? formatDate(rule.end_date) : 'No end'}
            </p>
          )}
        </div>
        {canModify && (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={onToggleActive}
              disabled={togglePending}
              className={cn(
                'p-2 rounded-lg transition-colors disabled:opacity-50',
                rule.is_active
                  ? 'text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20'
                  : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800',
              )}
              aria-label={rule.is_active ? 'Disable rule' : 'Enable rule'}
            >
              <PowerIcon />
            </button>
            <button type="button" onClick={onDuplicate} className="p-2 rounded-lg text-neutral-500 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors" aria-label="Duplicate rule">
              <CopyIcon />
            </button>
            <button type="button" onClick={onEdit} className="p-2 rounded-lg text-neutral-500 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors" aria-label="Edit rule">
              <PencilIcon />
            </button>
            <button type="button" onClick={onDelete} className="p-2 rounded-lg text-neutral-500 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors" aria-label="Delete rule">
              <TrashIcon />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function DiscountRulesContent() {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const discountTypeOptions = useMemo(() => buildDiscountTypeOptions(symbol), [symbol]);
  const [searchParams, setSearchParams] = useSearchParams();

  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canModify = roleLevel >= ROLE_LEVELS.OWNER;

  // Tab + filters
  const urlTab = (searchParams.get('tab') as TabKey) || 'ITEM_LEVEL';
  const urlStatus = searchParams.get('status') ?? '';
  const urlSearch = searchParams.get('search') ?? '';
  const urlPage = Number(searchParams.get('page') ?? '1');

  const [activeTab, setActiveTab] = useState<TabKey>(urlTab);
  const [statusFilter, setStatusFilter] = useState(urlStatus);
  const [page, setPage] = useState(urlPage);

  // Name search (debounced)
  const [searchInput, setSearchInput] = useState(urlSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Whether the in-table search / status filter is narrowing the result set.
  // Used to distinguish "no rules match this filter" from "no rules exist".
  const hasActiveFilters = statusFilter !== '' || debouncedSearch !== '';
  const clearFilters = useCallback(() => {
    setSearchInput('');
    setStatusFilter('');
    setPage(1);
  }, []);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (activeTab !== 'ITEM_LEVEL') params.tab = activeTab;
    if (statusFilter) params.status = statusFilter;
    if (debouncedSearch) params.search = debouncedSearch;
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [activeTab, statusFilter, debouncedSearch, page, setSearchParams]);

  // Data for rules tabs
  const [rules, setRules] = useState<DiscountRule[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Tab-wide Active / Scheduled counts for the KPI cards. These must reflect
  // every rule in the current tab — not just the page currently loaded into
  // `rules` — so the headline figures stay stable as the user paginates.
  const [tabCounts, setTabCounts] = useState({ active: 0, scheduled: 0 });

  // Product / Category options for multi-selects
  const [productOptions, setProductOptions] = useState<{ value: string; label: string }[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<{ value: string; label: string }[]>([]);
  // Every store, switched-off ones too, so a rule's saved store always shows (C10).
  const [storeLocations, setStoreLocations] = useState<Location[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  // Data for config tab
  const [config, setConfig] = useState<DiscountConfig | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [configDirty, setConfigDirty] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  // Per-role validation errors for the Max Discount (%) fields, keyed by role.
  const [roleLimitErrors, setRoleLimitErrors] = useState<Record<string, string>>({});

  // Fetch rules
  const fetchRules = useCallback(async () => {
    if (activeTab === 'ROLE_LIMITS') return;
    setIsLoading(true);
    try {
      const result = await discountRuleService.list({
        page,
        type: activeTab,
        status: statusFilter || undefined,
        search: debouncedSearch || undefined,
      });
      setRules(result.data);
      setTotalItems(result.meta.total);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load discount rules.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, page, statusFilter, debouncedSearch, toast]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  // Fetch tab-wide Active / Scheduled totals via dedicated count queries.
  // Reading `meta.total` (a COUNT over the whole tab) keeps the KPI cards
  // independent of pagination and the in-table search/status filters.
  // The Role Limits tab has no rule type of its own, so it counts every type.
  const fetchCounts = useCallback(async () => {
    const type = activeTab === 'ROLE_LIMITS' ? undefined : activeTab;
    try {
      const [active, scheduled] = await Promise.all([
        discountRuleService.list({ page: 1, limit: 1, type, status: 'ACTIVE' }),
        discountRuleService.list({ page: 1, limit: 1, type, status: 'SCHEDULED' }),
      ]);
      setTabCounts({ active: active.meta.total, scheduled: scheduled.meta.total });
    } catch {
      // Leave previous counts in place; the rules table surfaces load errors.
    }
  }, [activeTab]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  // Load product / category options once for multi-select fields
  useEffect(() => {
    let cancelled = false;
    setIsLoadingOptions(true);
    Promise.all([
      productService.list({ limit: 500, is_active: 'true' }).catch(() => ({ data: [] as { id: string; name: string }[] })),
      categoryService.list({ is_active: true }).catch(() => ({ data: [] as { id: string; name: string }[] })),
      locationService.list({ limit: 100 }).catch(() => ({ data: [] as Location[] })),
    ])
      .then(([products, categories, locations]) => {
        if (cancelled) return;
        setStoreLocations(locations.data ?? []);
        setProductOptions(
          (products.data as { id: string; name: string }[]).map((p) => ({ value: p.id, label: p.name })),
        );
        setCategoryOptions(
          (categories.data as { id: string; name: string }[]).map((c) => ({ value: c.id, label: c.name })),
        );
      })
      .finally(() => { if (!cancelled) setIsLoadingOptions(false); });
    return () => { cancelled = true; };
  }, []);

  // Fetch config — once on mount for the Role Limits KPI card, and again each
  // time the Role Limits tab opens.
  const configRequestedRef = useRef(false);
  useEffect(() => {
    if (activeTab !== 'ROLE_LIMITS' && configRequestedRef.current) return;
    configRequestedRef.current = true;
    setIsLoadingConfig(true);
    discountRuleService.getConfig()
      .then((c) => { setConfig(c); setConfigDirty(false); })
      .catch(() => {
        setConfig({
          role_limits: [
            { role: 'CASHIER', max_discount_percentage: 5 },
            { role: 'MANAGER', max_discount_percentage: 15 },
            { role: 'OWNER', max_discount_percentage: 100 },
          ],
          owner_override_allowed: true,
          reason_capture_mandatory: true,
        });
      })
      .finally(() => setIsLoadingConfig(false));
  }, [activeTab]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<DiscountRule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DiscountRule | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form fields
  const [formName, setFormName] = useState('');
  const [formRuleType, setFormRuleType] = useState<RuleType>('ITEM_LEVEL');
  const [formDiscountType, setFormDiscountType] = useState<DiscountType>('percentage');
  const [formValue, setFormValue] = useState('');
  const [formMinBill, setFormMinBill] = useState('');
  const [formMinQuantity, setFormMinQuantity] = useState('');
  const [formCustomerSegment, setFormCustomerSegment] = useState<CustomerSegment>('ALL');
  const [formProductIds, setFormProductIds] = useState<string[]>([]);
  const [formCategoryIds, setFormCategoryIds] = useState<string[]>([]);
  const [formStartDate, setFormStartDate] = useState(todayISO());
  const [formEndDate, setFormEndDate] = useState('');
  const [formStartTime, setFormStartTime] = useState('');
  const [formEndTime, setFormEndTime] = useState('');
  const [formDaysOfWeek, setFormDaysOfWeek] = useState<number[]>([]);
  const [formStackable, setFormStackable] = useState(false);
  const [formRequiresApproval, setFormRequiresApproval] = useState(false);
  const [formIsActive, setFormIsActive] = useState(true);
  const [formCombinesProduct, setFormCombinesProduct] = useState(true);
  const [formCombinesBill, setFormCombinesBill] = useState(true);
  const [formPromoType, setFormPromoType] = useState<PromoType>('percentage_off');
  const [formBuyQty, setFormBuyQty] = useState('');
  const [formGetQty, setFormGetQty] = useState('');
  const [formComboPrice, setFormComboPrice] = useState('');
  const [formComboProductIds, setFormComboProductIds] = useState<string[]>([]);
  const [formStoreScope, setFormStoreScope] = useState<StoreScope>('ALL');
  const [formLocationIds, setFormLocationIds] = useState<string[]>([]);
  const formStoreOptions = useMemo(() => storeOptions(storeLocations, formLocationIds), [storeLocations, formLocationIds]);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Inline action state
  const [togglingRuleId, setTogglingRuleId] = useState<string | null>(null);

  // Prefill captured once from the URL on first render. When arriving from an
  // Expiry Alerts "Discount" action (issue #231), the near-expiry product is
  // passed via query params so the "New Discount Rule" modal opens pre-filled
  // with that item. Captured up-front because the filter-sync effect rewrites
  // the URL (dropping these params) shortly after mount.
  const prefillProductRef = useRef(searchParams.get('product_id'));
  const prefillAppliedRef = useRef(false);

  const openCreateModal = useCallback(() => {
    setEditingRule(null);
    setFormName('');
    setFormRuleType(activeTab === 'ROLE_LIMITS' ? 'ITEM_LEVEL' : activeTab);
    setFormDiscountType('percentage');
    setFormValue('');
    setFormMinBill('');
    setFormMinQuantity('');
    setFormCustomerSegment('ALL');
    setFormProductIds([]);
    setFormCategoryIds([]);
    setFormStartDate(todayISO());
    setFormEndDate('');
    setFormStartTime('');
    setFormEndTime('');
    setFormDaysOfWeek([]);
    setFormStackable(false);
    setFormRequiresApproval(false);
    setFormIsActive(true);
    setFormCombinesProduct(true);
    setFormCombinesBill(true);
    setFormPromoType('percentage_off');
    setFormBuyQty('');
    setFormGetQty('');
    setFormComboPrice('');
    setFormComboProductIds([]);
    setFormStoreScope('ALL');
    setFormLocationIds([]);
    setFormError(null);
    setModalOpen(true);
  }, [activeTab]);

  // ── Prefill from Expiry Alerts "Discount" (issue #231) ────────────────────
  useEffect(() => {
    if (prefillAppliedRef.current) return;
    const productId = prefillProductRef.current;
    if (!productId || !canModify) return;
    prefillAppliedRef.current = true;
    openCreateModal();
    setFormProductIds([productId]);
  }, [canModify, openCreateModal]);

  const populateFormFromRule = useCallback((rule: DiscountRule) => {
    setFormName(rule.name);
    setFormRuleType(rule.rule_type);
    setFormDiscountType(rule.discount_type);
    setFormValue(rule.discount_type === 'flat' ? String(rule.value / subunit) : String(rule.value));
    setFormMinBill(rule.min_bill_amount > 0 ? String(rule.min_bill_amount / subunit) : '');
    setFormMinQuantity(rule.min_quantity ? String(rule.min_quantity) : '');
    setFormCustomerSegment(rule.customer_segment ?? 'ALL');
    setFormProductIds(rule.applicable_product_ids ?? []);
    setFormCategoryIds(rule.applicable_category_ids ?? []);
    setFormStartDate(rule.start_date?.split('T')[0] ?? todayISO());
    setFormEndDate(rule.end_date?.split('T')[0] ?? '');
    setFormStartTime(rule.start_time ?? '');
    setFormEndTime(rule.end_time ?? '');
    setFormDaysOfWeek(rule.days_of_week ?? []);
    setFormStackable(rule.stackable);
    setFormRequiresApproval(rule.requires_approval);
    setFormIsActive(rule.is_active);
    setFormCombinesProduct(rule.combines_with_product_discounts ?? true);
    setFormCombinesBill(rule.combines_with_bill_discounts ?? true);
    setFormPromoType(rule.promo_type ?? 'percentage_off');
    setFormBuyQty(rule.buy_quantity ? String(rule.buy_quantity) : '');
    setFormGetQty(rule.get_quantity ? String(rule.get_quantity) : '');
    setFormComboPrice(rule.combo_price ? String(rule.combo_price / subunit) : '');
    setFormComboProductIds(rule.combo_product_ids ?? []);
    const stores = scopeFromRule(rule.location_ids);
    setFormStoreScope(stores.scope);
    setFormLocationIds(stores.locationIds);
  }, [subunit]);

  const openEditModal = useCallback((rule: DiscountRule) => {
    setEditingRule(rule);
    populateFormFromRule(rule);
    setFormError(null);
    setModalOpen(true);
  }, [populateFormFromRule]);

  const openDuplicateModal = useCallback((rule: DiscountRule) => {
    setEditingRule(null);
    populateFormFromRule(rule);
    setFormName(`${rule.name} (Copy)`);
    setFormIsActive(false);
    setFormError(null);
    setModalOpen(true);
  }, [populateFormFromRule]);

  const handleToggleActive = useCallback(async (rule: DiscountRule) => {
    setTogglingRuleId(rule.id);
    const nextActive = !rule.is_active;
    // optimistic
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, is_active: nextActive, status: nextActive ? 'ACTIVE' : 'DISABLED' } : r)));
    try {
      await discountRuleService.update(rule.id, { is_active: nextActive });
      toast({ title: nextActive ? 'Rule enabled' : 'Rule disabled', variant: 'success' });
      fetchCounts();
    } catch (err) {
      // rollback
      setRules((prev) => prev.map((r) => (r.id === rule.id ? rule : r)));
      toast({ title: err instanceof ApiError ? err.message : 'Failed to update rule', variant: 'error' });
    } finally {
      setTogglingRuleId(null);
    }
  }, [toast, fetchCounts]);

  const handleSave = useCallback(async () => {
    if (!formName.trim()) { setFormError('Rule name is required'); return; }
    if (formName.trim().length < 2 || formName.trim().length > 200) { setFormError('Rule name must be 2–200 characters'); return; }
    const val = Number(formValue);

    if (formRuleType === 'PROMOTIONAL' && formPromoType === 'buy_x_get_y') {
      if (!formBuyQty || !formGetQty) { setFormError('Buy and Get quantities are required'); return; }
      if (!Number.isFinite(val) || val <= 0) { setFormError('Discount on the free item must be greater than zero'); return; }
      if (formDiscountType === 'percentage' && val > 100) { setFormError('Percentage cannot exceed 100%'); return; }
    } else if (formRuleType === 'PROMOTIONAL' && formPromoType === 'combo_price') {
      if (!formComboPrice) { setFormError('Combo price is required'); return; }
      if (formComboProductIds.length < 2) { setFormError('Select at least two combo products'); return; }
    } else {
      if (!Number.isFinite(val) || val <= 0) { setFormError('Discount value must be greater than zero'); return; }
      if (formDiscountType === 'percentage' && val > 100) { setFormError('Percentage cannot exceed 100%'); return; }
    }

    if (formStartDate && formEndDate && formEndDate < formStartDate) {
      setFormError('End date must be on or after start date'); return;
    }
    if (formStartTime && formEndTime && formEndTime <= formStartTime) {
      setFormError('End time must be after start time'); return;
    }
    const storeSave = locationIdsForSave(formStoreScope, formLocationIds);
    if ('error' in storeSave) { setFormError(storeSave.error); return; }

    setIsSaving(true);
    setFormError(null);

    try {
      const dto: CreateDiscountRuleDto = {
        name: formName.trim(),
        rule_type: formRuleType,
        discount_type: formDiscountType,
        value: formDiscountType === 'flat' ? Math.round(val * subunit) : val,
        min_bill_amount: formMinBill ? Math.round(Number(formMinBill) * subunit) : undefined,
        min_quantity: formMinQuantity ? Number(formMinQuantity) : undefined,
        customer_segment: formCustomerSegment !== 'ALL' ? formCustomerSegment : undefined,
        applicable_product_ids: formProductIds.length > 0 ? formProductIds : undefined,
        applicable_category_ids: formCategoryIds.length > 0 ? formCategoryIds : undefined,
        start_date: formStartDate || undefined,
        end_date: formEndDate || undefined,
        start_time: formStartTime || undefined,
        end_time: formEndTime || undefined,
        days_of_week: formDaysOfWeek.length > 0 ? formDaysOfWeek : undefined,
        stackable: formStackable,
        requires_approval: formRequiresApproval,
        is_active: formIsActive,
        combines_with_product_discounts: formCombinesProduct,
        combines_with_bill_discounts: formCombinesBill,
        // Always sent: [] widens a store-only rule back to every store (C10).
        location_ids: storeSave.locationIds,
      };

      if (formRuleType === 'PROMOTIONAL') {
        dto.promo_type = formPromoType;
        if (formPromoType === 'buy_x_get_y') {
          dto.buy_quantity = Number(formBuyQty);
          dto.get_quantity = Number(formGetQty);
        }
        if (formPromoType === 'combo_price') {
          dto.combo_price = Math.round(Number(formComboPrice) * subunit);
          dto.combo_product_ids = formComboProductIds;
        }
      }

      if (editingRule) {
        await discountRuleService.update(editingRule.id, dto);
        toast({ title: 'Rule updated', variant: 'success' });
      } else {
        await discountRuleService.create(dto);
        toast({ title: `Discount rule '${formName.trim()}' created`, variant: 'success' });
      }
      setModalOpen(false);
      fetchRules();
      fetchCounts();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to save rule');
    } finally {
      setIsSaving(false);
    }
  }, [formName, formRuleType, formDiscountType, formValue, formMinBill, formMinQuantity, formCustomerSegment, formProductIds, formCategoryIds, formStartDate, formEndDate, formStartTime, formEndTime, formDaysOfWeek, formStackable, formRequiresApproval, formIsActive, formCombinesProduct, formCombinesBill, formPromoType, formBuyQty, formGetQty, formComboPrice, formComboProductIds, formStoreScope, formLocationIds, editingRule, subunit, fetchRules, fetchCounts, toast]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await discountRuleService.delete(deleteTarget.id);
      toast({ title: 'Rule deleted', variant: 'success' });
      setDeleteTarget(null);
      fetchRules();
      fetchCounts();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to delete rule', variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, fetchRules, fetchCounts, toast]);

  // Save config
  const handleSaveConfig = useCallback(async () => {
    if (!config) return;
    if (Object.keys(roleLimitErrors).length > 0) {
      toast({ title: 'Fix the highlighted role limits before saving', variant: 'error' });
      return;
    }
    setIsSavingConfig(true);
    try {
      await discountRuleService.updateConfig(config);
      toast({ title: 'Discount configuration saved', variant: 'success' });
      setConfigDirty(false);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to save configuration', variant: 'error' });
    } finally {
      setIsSavingConfig(false);
    }
  }, [config, roleLimitErrors, toast]);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === 'n' && !modalOpen && !deleteTarget && canModify && activeTab !== 'ROLE_LIMITS') {
        e.preventDefault();
        openCreateModal();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, deleteTarget, canModify, openCreateModal, activeTab]);

  useEffect(() => { document.title = 'Discount Rules — Orio360 Admin'; }, []);

  // Stats
  const stats = useMemo(
    () => ({
      active: tabCounts.active,
      scheduled: tabCounts.scheduled,
      configuredRoles: config?.role_limits.length ?? 0,
    }),
    [tabCounts, config],
  );

  // Table columns
  const columns: ColumnDef<DiscountRule>[] = useMemo(() => {
    const cols: ColumnDef<DiscountRule>[] = [
      {
        key: 'name', header: 'Name',
        render: (_v: unknown, row: DiscountRule) => (
          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.name}</span>
        ),
      },
      {
        key: 'discount_type', header: 'Type',
        render: (_v: unknown, row: DiscountRule) => (
          <Badge variant="draft" size="sm">{typeLabel(row)}</Badge>
        ),
      },
      {
        key: 'value', header: 'Value', numeric: true,
        render: (_v: unknown, row: DiscountRule) => (
          <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
            {displayValue(row, currency)}
          </span>
        ),
      },
      {
        key: 'applicable', header: 'Applies To',
        render: (_v: unknown, row: DiscountRule) => (
          <div className="flex gap-1">
            {row.promo_type === 'combo_price' && (row.combo_product_ids ?? []).length > 0 && (
              <Badge variant="info" size="sm">{row.combo_product_ids.length} combo products</Badge>
            )}
            {row.applicable_product_ids.length > 0 && (
              <Badge variant="info" size="sm">{row.applicable_product_ids.length} products</Badge>
            )}
            {row.applicable_category_ids.length > 0 && (
              <Badge variant="info" size="sm">{row.applicable_category_ids.length} categories</Badge>
            )}
            {row.promo_type !== 'combo_price' && row.applicable_product_ids.length === 0 && row.applicable_category_ids.length === 0 && (
              <span className="text-xs text-neutral-500 dark:text-neutral-400">All items</span>
            )}
            {storeCountLabel(row.location_ids?.length ?? 0) && (
              <Badge variant="info" size="sm">{storeCountLabel(row.location_ids?.length ?? 0)}</Badge>
            )}
          </div>
        ),
      },
      {
        key: 'status', header: 'Status',
        render: (_v: unknown, row: DiscountRule) => (
          <Badge variant={STATUS_BADGE[row.status] ?? 'draft'} size="sm">{row.status}</Badge>
        ),
      },
      {
        key: 'date_range', header: 'Date Range',
        render: (_v: unknown, row: DiscountRule) => (
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {row.start_date ? formatDate(row.start_date) : '—'} → {row.end_date ? formatDate(row.end_date) : '∞'}
          </span>
        ),
      },
    ];

    if (canModify) {
      cols.push({
        key: 'actions', header: '',
        render: (_v: unknown, row: DiscountRule) => (
          <div className="flex items-center gap-1 justify-end">
            <button
              type="button"
              onClick={() => handleToggleActive(row)}
              disabled={togglingRuleId === row.id}
              className={cn(
                'p-2 rounded-lg transition-colors disabled:opacity-50',
                row.is_active
                  ? 'text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20'
                  : 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-800',
              )}
              aria-label={row.is_active ? `Disable ${row.name}` : `Enable ${row.name}`}
              title={row.is_active ? 'Disable rule' : 'Enable rule'}
            >
              <PowerIcon />
            </button>
            <button type="button" onClick={() => openDuplicateModal(row)} className="p-2 rounded-lg text-neutral-500 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors" aria-label={`Duplicate ${row.name}`} title="Duplicate rule">
              <CopyIcon />
            </button>
            <button type="button" onClick={() => openEditModal(row)} className="p-2 rounded-lg text-neutral-500 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors" aria-label={`Edit ${row.name}`}>
              <PencilIcon />
            </button>
            <button type="button" onClick={() => setDeleteTarget(row)} className="p-2 rounded-lg text-neutral-500 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors" aria-label={`Delete ${row.name}`}>
              <TrashIcon />
            </button>
          </div>
        ),
      });
    }

    return cols;
  }, [canModify, openEditModal, openDuplicateModal, handleToggleActive, togglingRuleId, currency]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const isRuleTab = activeTab !== 'ROLE_LIMITS';

  const successTone = toneClasses('success');
  const brandTone = toneClasses('brand');
  const violetTone = toneClasses('violet');

  // Is promotional subtype needing special fields?
  const showBuyGetFields = formRuleType === 'PROMOTIONAL' && formPromoType === 'buy_x_get_y';
  const showComboFields = formRuleType === 'PROMOTIONAL' && formPromoType === 'combo_price';
  // Buy X Get Y keeps the value field: it is the discount on each free item.
  const showValueField = !showComboFields;

  return (
    <PageContainer
      title="Discount Rules"
      subtitle="Configure discounts, promotions, and role-based limits"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Discounts' },
      ]}
      actions={
        canModify && isRuleTab ? (
          <Button variant="primary" size="sm" onClick={openCreateModal}>
            <PlusIcon /><span className="ml-1.5">New Discount Rule</span>
          </Button>
        ) : undefined
      }
    >
      <div className="animate-fade-in">
        {/* Header Gradient */}
        <div className="bg-gradient-to-r from-warning-600 to-danger-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(245,158,11,0.45)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
          <div className="relative">
            <h1 className="text-2xl font-bold text-white">Discount Rules</h1>
            <p className="text-warning-50/90 mt-1">Configure discounts, promotions, and role-based limits</p>
          </div>
        </div>

        {/* KPI Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden')}>
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', successTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', successTone.icon)}>
                  <PercentIcon className="h-5 w-5" />
                </div>
                <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Active Rules</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-white">{stats.active}</p>
            </div>
          </div>
          <div className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden')} style={{ animationDelay: '50ms' }}>
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', brandTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', brandTone.icon)}>
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Scheduled</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-white">{stats.scheduled}</p>
            </div>
          </div>
          <div className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden')} style={{ animationDelay: '100ms' }}>
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', violetTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', violetTone.icon)}>
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1z" /></svg>
                </div>
                <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Role Limits</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-white">{stats.configuredRoles}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <Tabs
            items={TABS.map((t) => ({ id: t.key, label: t.label }))}
            activeId={activeTab}
            onChange={(id) => { setActiveTab(id as TabKey); setPage(1); }}
            variant="segmented"
            scrollable
          />
        </div>

        {/* Rule Tabs Content */}
        {isRuleTab && (
          <>
            {/* Filter Bar */}
            <div className={cn(GLASS_FILTER, 'p-4 mb-6')}>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="sm:flex-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                    Search by name
                  </label>
                  <Input
                    type="text"
                    value={searchInput}
                    onChange={(val) => { setSearchInput(val); setPage(1); }}
                    placeholder="e.g. Diwali, BOGO, flat 100 off"
                  />
                </div>
                <div className="w-full sm:w-44">
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                    Status
                  </label>
                  <Select
                    options={STATUS_OPTIONS}
                    value={statusFilter}
                    onChange={(val) => { setStatusFilter(typeof val === 'string' ? val : val[0] ?? ''); setPage(1); }}
                    placeholder="All Statuses"
                  />
                </div>
              </div>
            </div>

            {isLoading ? (
              <div className={cn(GLASS, 'p-6 space-y-4')}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex gap-4">
                    <Skeleton variant="text" width="20%" height={18} />
                    <Skeleton variant="rect" width={70} height={24} />
                    <Skeleton variant="text" width="10%" height={18} />
                    <Skeleton variant="text" width="15%" height={18} />
                    <Skeleton variant="rect" width={70} height={24} />
                    <Skeleton variant="text" width="15%" height={18} />
                  </div>
                ))}
              </div>
            ) : rules.length === 0 ? (
              hasActiveFilters ? (
                <EmptyState
                  variant="page"
                  icon={<PercentEmptyIcon />}
                  title="No rules match these filters"
                  description="No discount rules match the current search or status filter. Existing rules may be hidden — clear the filters to see them all."
                  action={{ label: 'Clear filters', onClick: clearFilters }}
                />
              ) : (
                <EmptyState
                  variant="page"
                  icon={<PercentEmptyIcon />}
                  title="No discount rules configured"
                  description="Create rules to configure item-level, bill-level, and promotional discounts"
                  action={canModify ? { label: 'Create Discount Rule', onClick: openCreateModal } : undefined}
                />
              )
            ) : (
              <>
                <div className={cn(GLASS, 'overflow-hidden hidden md:block mb-6')}>
                  <div className="px-5 py-4">
                    <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                      Rules ({totalItems.toLocaleString('en-IN')})
                    </h3>
                  </div>
                  <DataTable<DiscountRule> columns={columns} data={rules} />
                </div>

                <div className="block md:hidden space-y-3 mb-6">
                  {rules.map((rule) => (
                    <RuleMobileCard
                      key={rule.id}
                      rule={rule}
                      canModify={canModify}
                      onEdit={() => openEditModal(rule)}
                      onDelete={() => setDeleteTarget(rule)}
                      onDuplicate={() => openDuplicateModal(rule)}
                      onToggleActive={() => handleToggleActive(rule)}
                      togglePending={togglingRuleId === rule.id}
                    />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="mb-6">
                    <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} pageSize={PAGE_SIZE} onPageChange={setPage} />
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Role Limits Tab Content */}
        {activeTab === 'ROLE_LIMITS' && (
          isLoadingConfig ? (
            <div className={cn(GLASS, 'p-6 space-y-4')}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} variant="rect" width="100%" height={80} />
              ))}
            </div>
          ) : config ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {config.role_limits.map((rl, idx) => (
                  <div key={rl.role} className={cn(GLASS_FORM, 'p-5 animate-slide-up')} style={{ animationDelay: `${idx * 50}ms` }}>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant={rl.role === 'OWNER' ? 'info' : rl.role === 'MANAGER' ? 'warning' : 'draft'} size="sm">{rl.role}</Badge>
                    </div>
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                      Max Discount (%)
                    </label>
                    <Input
                      type="number"
                      error={roleLimitErrors[rl.role]}
                      value={Number.isNaN(rl.max_discount_percentage) ? '' : String(rl.max_discount_percentage)}
                      onChange={(val) => {
                        const trimmed = val.trim();
                        const isValid = /^\d+$/.test(trimmed) && Number(trimmed) <= 100;
                        setRoleLimitErrors((prev) => {
                          const next = { ...prev };
                          if (isValid) delete next[rl.role];
                          else next[rl.role] = 'Max discount must be a whole number between 0 and 100.';
                          return next;
                        });
                        // Preserve what the user typed: empty becomes NaN (rendered
                        // as a blank field for correction), never a silent 0.
                        const v = trimmed === '' ? NaN : Number(trimmed);
                        setConfig((prev) => {
                          if (!prev) return prev;
                          const updated = { ...prev, role_limits: prev.role_limits.map((r, i) => i === idx ? { ...r, max_discount_percentage: v } : r) };
                          return updated;
                        });
                        setConfigDirty(true);
                      }}
                    />
                  </div>
                ))}
              </div>

              <div className={cn(GLASS_FORM, 'p-5 space-y-4')}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Allow Owner Override</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Saved, but not yet enforced at the till</p>
                  </div>
                  <Toggle
                    checked={config.owner_override_allowed}
                    onChange={(v) => { setConfig((prev) => prev ? { ...prev, owner_override_allowed: v } : prev); setConfigDirty(true); }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Require Discount Reason</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Mandatory justification for any discount applied</p>
                  </div>
                  <Toggle
                    checked={config.reason_capture_mandatory}
                    onChange={(v) => { setConfig((prev) => prev ? { ...prev, reason_capture_mandatory: v } : prev); setConfigDirty(true); }}
                  />
                </div>
              </div>

              {configDirty && (
                <div className="flex justify-end">
                  <Button variant="primary" size="sm" onClick={handleSaveConfig} disabled={isSavingConfig || Object.keys(roleLimitErrors).length > 0}>
                    {isSavingConfig ? 'Saving...' : 'Save Configuration'}
                  </Button>
                </div>
              )}
            </div>
          ) : null
        )}
      </div>

      {/* Mobile FAB */}
      {canModify && isRuleTab && (
        <button
          type="button"
          onClick={openCreateModal}
          className="fixed bottom-6 right-6 md:hidden z-40 w-14 h-14 rounded-full bg-gradient-to-br from-warning-500 to-danger-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center active:scale-95"
          aria-label="New Discount Rule"
        >
          <PlusIcon />
        </button>
      )}

      {/* Create/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingRule ? 'Edit Discount Rule' : 'New Discount Rule'} variant="wide">
        <div className="space-y-5 p-1 max-h-[75vh] overflow-y-auto">
          {formError && (
            <div className="text-xs text-danger-600 bg-danger-50 dark:bg-danger-900/20 px-3 py-2 rounded-lg">{formError}</div>
          )}

          {/* Section 1: Basic */}
          {/* Decreasing z-index per section so a Select dropdown opening
              downward paints above the following glass fieldsets (each
              backdrop-blur fieldset is its own stacking context). */}
          <fieldset className={cn(GLASS_FORM, 'relative z-40 p-5')}>
            <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3 px-2">
              Basic
            </legend>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Rule Name *</label>
                <Input value={formName} onChange={(val) => { setFormName(val); setFormError(null); }} placeholder="e.g. Diwali 20% Off" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Rule Type *</label>
                  <Select options={RULE_TYPE_OPTIONS} value={formRuleType} onChange={(v) => setFormRuleType((typeof v === 'string' ? v : v[0] ?? 'ITEM_LEVEL') as RuleType)} placeholder="Select type" />
                </div>
                {formRuleType === 'PROMOTIONAL' && (
                  <div>
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Promo Type *</label>
                    <Select options={PROMO_TYPE_OPTIONS} value={formPromoType} onChange={(v) => {
                      const next = (typeof v === 'string' ? v : v[0] ?? 'percentage_off') as PromoType;
                      setFormPromoType(next);
                      // Buy X Get Y starts with the free item at 100 % off.
                      if (next === 'buy_x_get_y' && !formValue) { setFormDiscountType('percentage'); setFormValue('100'); }
                    }} placeholder="Select promo type" />
                  </div>
                )}
              </div>
            </div>
          </fieldset>

          {/* Section 2: Value */}
          <fieldset className={cn(GLASS_FORM, 'relative z-30 p-5')}>
            <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3 px-2">
              Discount Value
            </legend>
            <div className="space-y-4">
              {showValueField && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <RadioGroup
                      label="Discount Type *"
                      variant="horizontal"
                      options={discountTypeOptions.map((o) => ({ value: o.value, label: o.label }))}
                      value={formDiscountType}
                      onChange={(v) => setFormDiscountType(v as DiscountType)}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
                      {showBuyGetFields ? 'Discount on each free item' : 'Value'} ({formDiscountType === 'percentage' ? '%' : symbol}) *
                    </label>
                    <Input type="number" value={formValue} onChange={(val) => { setFormValue(val); setFormError(null); }} placeholder={formDiscountType === 'percentage' ? 'e.g. 10' : 'e.g. 50'} />
                  </div>
                </div>
              )}

              {showBuyGetFields && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Buy Quantity (X) *</label>
                    <Input type="number" value={formBuyQty} onChange={(val) => setFormBuyQty(val)} placeholder="e.g. 3" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Get Quantity (Y) *</label>
                    <Input type="number" value={formGetQty} onChange={(val) => setFormGetQty(val)} placeholder="e.g. 1" />
                  </div>
                </div>
              )}

              {showComboFields && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">{`Combo Price (${symbol}) *`}</label>
                    <Input type="number" value={formComboPrice} onChange={(val) => setFormComboPrice(val)} placeholder="e.g. 299" />
                  </div>
                  <Select
                    label="Combo Products *"
                    variant="multi-select"
                    placeholder={isLoadingOptions ? 'Loading…' : 'Select combo products'}
                    options={productOptions}
                    value={formComboProductIds}
                    onChange={(v) => { setFormComboProductIds(Array.isArray(v) ? (v as string[]) : []); setFormError(null); }}
                  />
                </div>
              )}
            </div>
          </fieldset>

          {/* Section 3: Applicability & Conditions */}
          <fieldset className={cn(GLASS_FORM, 'relative z-20 p-5')}>
            <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3 px-2">
              Applicability & Conditions
            </legend>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Select
                  label="Applicable Products"
                  variant="multi-select"
                  placeholder={isLoadingOptions ? 'Loading…' : 'All products'}
                  options={productOptions}
                  value={formProductIds}
                  onChange={(v) => setFormProductIds(Array.isArray(v) ? (v as string[]) : [])}
                />
                <Select
                  label="Applicable Categories"
                  variant="multi-select"
                  placeholder={isLoadingOptions ? 'Loading…' : 'All categories'}
                  options={categoryOptions}
                  value={formCategoryIds}
                  onChange={(v) => setFormCategoryIds(Array.isArray(v) ? (v as string[]) : [])}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">{`Min Bill Amount (${symbol})`}</label>
                  <Input type="number" value={formMinBill} onChange={(val) => setFormMinBill(val)} placeholder="0 = No minimum" />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Min Quantity</label>
                  <Input type="number" value={formMinQuantity} onChange={(val) => setFormMinQuantity(val)} placeholder="No minimum" />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Customer Segment</label>
                  <Select options={CUSTOMER_SEGMENT_OPTIONS} value={formCustomerSegment} onChange={(v) => setFormCustomerSegment((typeof v === 'string' ? v : v[0] ?? 'ALL') as CustomerSegment)} />
                </div>
              </div>
              {/* C10: all stores, or only the stores ticked here */}
              <div>
                <span id="rule-stores-label" className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Stores</span>
                <div role="radiogroup" aria-labelledby="rule-stores-label" className="flex flex-wrap gap-2">
                  {STORE_SCOPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={formStoreScope === opt.value}
                      onClick={() => { setFormStoreScope(opt.value); setFormError(null); }}
                      className={cn(
                        'px-4 py-1.5 rounded-full text-sm font-medium border transition-all duration-200',
                        formStoreScope === opt.value
                          ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white border-transparent shadow-md'
                          : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 border-neutral-200 dark:border-neutral-700 hover:border-brand-400 hover:bg-brand-50 dark:hover:bg-neutral-700',
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {formStoreScope === 'SELECTED' && (
                  <div className="mt-3 space-y-2 rounded-lg border border-indigo-100 dark:border-neutral-700 bg-indigo-50/40 dark:bg-neutral-900/40 p-3 animate-fade-in">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">The rule works only at the stores you tick.</p>
                    {formStoreOptions.length === 0 ? (
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">{isLoadingOptions ? 'Loading…' : 'No stores to choose.'}</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {formStoreOptions.map((store) => (
                          <Checkbox
                            key={store.id}
                            label={store.label}
                            checked={formLocationIds.includes(store.id)}
                            onChange={(on) => {
                              setFormLocationIds((ids) => (on ? [...ids, store.id] : ids.filter((id) => id !== store.id)));
                              setFormError(null);
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </fieldset>

          {/* Section 4: Schedule & Behavior */}
          <fieldset className={cn(GLASS_FORM, 'relative z-10 p-5')}>
            <legend className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3 px-2">
              Schedule & Behavior
            </legend>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Start Date</label>
                  <DatePicker
                    value={formStartDate ? new Date(`${formStartDate}T00:00:00`) : undefined}
                    onChange={(d: Date) => setFormStartDate(toLocalIsoDate(d))}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">End Date</label>
                  <DatePicker
                    value={formEndDate ? new Date(`${formEndDate}T00:00:00`) : undefined}
                    onChange={(d: Date) => setFormEndDate(toLocalIsoDate(d))}
                  />
                </div>
              </div>

              <ScheduleFields
                startTime={formStartTime}
                endTime={formEndTime}
                daysOfWeek={formDaysOfWeek}
                onStartTimeChange={setFormStartTime}
                onEndTimeChange={setFormEndTime}
                onDaysChange={setFormDaysOfWeek}
              />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <div>
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Active</p>
                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400">Applied at checkout and on quotations</p>
                  </div>
                  <Toggle checked={formIsActive} onChange={setFormIsActive} />
                </label>
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <div>
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Stackable</p>
                    {/* Pricing takes the single largest discount for a line, so
                        this flag is not read. Left visible rather than removed
                        so an existing setting is not silently discarded. */}
                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400">Not used — the largest discount wins</p>
                  </div>
                  <Toggle checked={formStackable} onChange={setFormStackable} />
                </label>
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <div>
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Requires Approval</p>
                    {/* Discount-override approval exists and works end to end,
                        but it is triggered by the cashier's role cap, not by
                        this flag. Do not promise a sign-off that will not come. */}
                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400">Not yet used — approval follows the role limits below</p>
                  </div>
                  <Toggle checked={formRequiresApproval} onChange={setFormRequiresApproval} />
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <div>
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Combines with product discounts</p>
                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                      Off: never used with other item offers — the bill gets whichever gives more off
                    </p>
                  </div>
                  <Toggle checked={formCombinesProduct} onChange={setFormCombinesProduct} />
                </label>
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                  <div>
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Combines with bill discounts</p>
                    <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                      Off: never used with a Bill-Level rule — the bill gets whichever gives more off
                    </p>
                  </div>
                  <Toggle checked={formCombinesBill} onChange={setFormCombinesBill} />
                </label>
              </div>
            </div>
          </fieldset>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)} disabled={isSaving}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Discount Rule"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete Rule"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={isDeleting}
      />
    </PageContainer>
  );
}

export function DiscountRulesPage() {
  return (
    <ErrorBoundary>
      <DiscountRulesContent />
    </ErrorBoundary>
  );
}
